import Axios from 'axios';
import { openReport } from '../../../utils/sessionGuard'; // セッション失効時は新タブを開かず現在の画面でログインへ
import React, { useEffect, useState, useCallback } from 'react';
import config from '../../Config/key';
import {
  Table,
  Divider,
  Tag,
  Button,
  message,
  Modal,
  Progress,
  Input,
  Alert,
} from 'antd';
// 共用コンポーネント（デザインシステム Ph.3）
// テーブル強化 PJ Ph.3 拡大: 列リサイズ + ソート + 省略
import {
  PageContainer,
  PageHeader,
  useResizableColumns,
  ellipsisColumn,
  textSorter,
} from '../../common';

function MakeReportPage() {
  const [projectKey, setProjectKey] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [pdfProgress, setPdfProgress] = useState(0);

  // 「プロジェクト削除」モーダル用 state
  const [deleteTargetProject, setDeleteTargetProject] = useState(null);
  const [deleteEchoInput, setDeleteEchoInput] = useState('');
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  const readDB = async () => {
    const res = await Axios.get('/api/projectKey');
    let projectData = res.data;
    // console.log({ res });
    setProjectKey(projectData);
  };

  useEffect(() => {
    readDB();
    //console.log({ projectKey });
  }, []);

  // ============================================================
  // PDF ZIP ダウンロード（内部ヘルパー）
  // ------------------------------------------------------------
  // S3 に集まった PDF を ZIP にまとめて受け取り、ブラウザのダウンロードをトリガーする。
  // smartDownloadHandler から呼ばれる（即時ダウンロード / SSE 完了後の自動ダウンロード両方）。
  // ============================================================
  const downloadZipReport = async (projectId) => {
    try {
      const res = await Axios.get(`/api/downloadZipReport/${projectId}`, {
        responseType: 'arraybuffer',
        headers: { Accept: 'application/zip' },
      });
      if (res.data) {
        const blob = new Blob([res.data], { type: 'application/zip' });
        const fileDownloadUrl = URL.createObjectURL(blob);
        const tempLink = document.createElement('a');
        tempLink.href = fileDownloadUrl;
        tempLink.download = 'Recurrent_10010_Survey_Report.zip';
        tempLink.click();
        tempLink.remove();
      }
      return true;
    } catch (error) {
      console.log(error);
      const detail =
        (error.response && error.response.data && error.response.data.error) ||
        error.message ||
        'ZIPダウンロードに失敗しました。';
      message.error({ content: detail, key: 'makecsv', duration: 5 });
      return false;
    }
  };

  // ============================================================
  // スマートダウンロード — 旧「レポート作成」+「レポートダウンロード」の自動化統合
  // ------------------------------------------------------------
  // 常に SSE を経由する。バックエンドの SSE ハンドラは「S3 に無い PDF のみ生成」
  // するため:
  //   - 全 PDF が既に存在 → SSE が即時完了 → そのまま ZIP ダウンロード
  //   - 一部 / 全部が未生成 → 欠落分のみ生成 → 進捗表示 → 完了後に ZIP ダウンロード
  // これにより「make_report_date はあるが S3 が空」のような不整合状態も
  // 自動的に補完される（過去の「レポート削除」で S3 のみ空になったケース等）。
  // ============================================================
  const smartDownloadHandler = async (record, e) => {
    e.preventDefault();
    const projectId = record._id;

    setModalVisible(true);
    setPdfProgress(0);
    setButtonDisabled(true);

    let eventSource;
    try {
      // make_report_date を記録（生成開始時刻の記録。表示用に維持）
      const res = await Axios.patch(
        `/api/projectKey/${projectId}/makeReportDate`,
        { make_report_date: Date.now() }
      );
      readDB();
      const { user_cnt, _id } = res.data.project;

      // SSE で欠落 PDF のみ生成し進捗を受信
      // ※ EventSource はカスタムヘッダー (X-API-Key) を送れないため、
      //   API キーをクエリパラメータで渡す（apiKey ミドルウェアは req.query.apiKey も許可）。
      const apiKeyQuery = config.API_KEY
        ? `?apiKey=${encodeURIComponent(config.API_KEY)}`
        : '';
      eventSource = new EventSource(
        `${config.BACKEND_SERVER}/api/evaluationPower/${user_cnt}/${_id}${apiKeyQuery}`
      );
      eventSource.onmessage = async (ev) => {
        const status = JSON.parse(ev.data);
        const progress =
          status.userCnt > 0
            ? parseInt((status.finish / status.userCnt) * 100)
            : 100;
        setPdfProgress(progress);
        if (status.finish >= status.userCnt) {
          eventSource.close();
          setButtonDisabled(false);
          // 全 PDF が揃ったので ZIP ダウンロードを開始
          await downloadZipReport(projectId);
          // ダウンロード開始後にモーダルを自動で閉じる
          setModalVisible(false);
          setPdfProgress(0);
        }
      };
      eventSource.onerror = () => {
        eventSource.close();
        setButtonDisabled(false);
        setModalVisible(false);
        message.error({
          content:
            'PDF 生成中に通信エラーが発生しました。再度お試しください。',
          key: 'makecsv',
          duration: 5,
        });
      };
    } catch (error) {
      console.log({ error });
      if (eventSource) eventSource.close();
      setModalVisible(false);
      setButtonDisabled(false);
      const detail =
        (error.response && error.response.data && error.response.data.error) ||
        error.message ||
        'レポート作成に失敗しました。';
      message.error({ content: detail, key: 'makecsv', duration: 5 });
    }
  };

  // ============================================================
  // プロジェクト一括削除 — モーダル制御
  // ------------------------------------------------------------
  // 旧「レポート削除」ボタンを置換。S3 PDF だけでなく ProjectKey ドキュメントと
  // EvaluationPower 全件まで一括削除する。回復不可作業のため echo 入力 + 確認モーダル必須。
  // ============================================================
  const openDeleteProjectModal = (record, e) => {
    e.preventDefault();
    setDeleteTargetProject(record);
    setDeleteEchoInput('');
  };

  const cancelDeleteProjectModal = () => {
    if (deleteInProgress) return; // 処理中は閉じる操作を無効化
    setDeleteTargetProject(null);
    setDeleteEchoInput('');
  };

  const confirmDeleteProject = async () => {
    if (!deleteTargetProject) return;
    const expected = (deleteTargetProject.project_key || '').trim();
    if (deleteEchoInput.trim() !== expected) {
      message.error({
        content:
          'プロジェクトキー入力が一致しません。正確に入力してください。',
        key: 'projectdelete',
        duration: 3,
      });
      return;
    }
    setDeleteInProgress(true);
    try {
      const executorToken =
        (typeof window !== 'undefined' && localStorage.getItem('w_auth')) || '';
      const res = await Axios.post(
        `/api/projectKey/${deleteTargetProject._id}/delete`,
        {
          confirm: 'YES_DELETE_PROJECT',
          projectKeyEcho: deleteEchoInput,
          executorToken,
        }
      );
      if (res.data && res.data.success) {
        const { deleted_evaluations, deleted_s3_objects, s3_warning } =
          res.data;
        message.success({
          content: `プロジェクトを削除しました（EvaluationPower ${deleted_evaluations}件 / S3 PDF ${deleted_s3_objects}件）${
            s3_warning ? ' ※' + s3_warning : ''
          }`,
          key: 'projectdelete',
          duration: 4,
        });
        setDeleteTargetProject(null);
        setDeleteEchoInput('');
        readDB();
      } else {
        message.error({
          content: (res.data && res.data.error) || '削除に失敗しました',
          key: 'projectdelete',
          duration: 4,
        });
      }
    } catch (error) {
      console.log({ error });
      const detail =
        (error.response && error.response.data && error.response.data.error) ||
        error.message ||
        'プロジェクト削除に失敗しました';
      message.error({
        content: detail,
        key: 'projectdelete',
        duration: 5,
      });
    } finally {
      setDeleteInProgress(false);
    }
  };
  // 旧 downloadReportHandler は smartDownloadHandler + downloadZipReport に統合済み（廃止）

  // const downloadCompanyReportHandler = async (projectId, e) => {
  //   e.preventDefault();
  //   try {
  //     console.log('Company Report Download');
  //   } catch (error) {
  //     console.log({ error });
  //   }
  // };

  const downloadCompanyReportHandler = async (project_id, e) => {
    e.preventDefault();
    try {
      // setModalVisible(true);
      // message.success({
      //   content: 'ファイル生成中！',
      //   key: 'makecsv',
      //   duration: 4,
      // });

      // console.log({ sendData });
      const res = await Axios.post(
        '/api/dbModel100/makeReportExternal10010_company',
        { project_id: project_id }
      );
      // サーバープロキシ経由でPDFを表示（S3 URLを露出しない）
      openReport(res.data.tokenId);
      // console.log({ url });
      // setTimeout(() => {
      //   message.success({
      //     content: 'ファイル生成完了！',
      //     key: 'makecsv',
      //     duration: 4,
      //   });
      //   // setModalVisible(false);

      //   window.open(url, '_blank');
      // }, 3000);
    } catch (error) {
      console.log(error.message);
      setModalVisible(false);
      message.error({
        content: error.message,
        key: 'makecsv',
        duration: 4,
      });
    }
  };

  const handleModalOk = async (e) => {
    //setLoading(true);
    setModalVisible(false);
    setButtonDisabled(true);
    setPdfProgress(0);
  };

  // テーブル強化 PJ Ph.3 拡大: <Column> → columns 配列。テキスト列はソート + 省略、操作列は維持。
  const rawColumns = [
    ellipsisColumn({
      title: 'プロジェクトキー',
      dataIndex: 'project_key',
      key: 'project_key',
      width: 240,
      sorter: textSorter('project_key'),
    }),
    ellipsisColumn({
      title: 'プロジェクト説明',
      dataIndex: 'project_description',
      key: 'project_description',
      width: 300,
      sorter: textSorter('project_description'),
    }),
    {
      title: '作業',
      key: 'action',
      width: 420,
      render: (text, record) => (
        <span>
          {/* レポートダウンロード — smartDownloadHandler が PDF 未作成時は自動生成 + ダウンロード */}
          <Button
            type="primary"
            size="small"
            onClick={(e) => smartDownloadHandler(record, e)}
          >
            レポートダウンロード
          </Button>
          <Divider type="vertical" />
          <Button
            size="small"
            onClick={(e) => downloadCompanyReportHandler(record._id, e)}
          >
            組織診断レポートダウンロード
          </Button>
          <Divider type="vertical" />
          {/* プロジェクト削除 — 危険操作のため行の右端に配置 */}
          <Button
            type="primary"
            danger
            size="small"
            onClick={(e) => openDeleteProjectModal(record, e)}
          >
            プロジェクト削除
          </Button>
        </span>
      ),
    },
  ];

  const { columns, components, scroll, wrapRef } = useResizableColumns(rawColumns);

  // return <div>User Management Page2</div>;
  return (
    // 大量テーブル（プロジェクト一覧）ページのため fluid（全幅）。
    // 広幅モニターでデータ密度を確保（デザインシステム「大量テーブル」例外）。
    <PageContainer fluid style={{ marginTop: 'var(--space-6)' }}>
      <PageHeader
        title="レポート作成"
        subtitle="人事評価力診断プロジェクトのレポートを生成・ダウンロードします。"
      />
      <div ref={wrapRef}>
        <Table
          columns={columns}
          components={components}
          scroll={scroll}
          dataSource={projectKey}
          size="middle"
          rowKey="project_key"
        />
      </div>
      <Modal
        title="レポート生成"
        open={modalVisible}
        onOk={handleModalOk}
        footer={[
          <Button
            key="submit"
            type="primary"
            disabled={buttonDisabled}
            onClick={handleModalOk}
          >
            OK
          </Button>,
        ]}
      >
        <div
          style={{
            height: '12em',
            position: 'relative',
          }}
        >
          <div
            style={{
              margin: '0',
              height: '10em',
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginRight: '-50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            {pdfProgress < 100
              ? 'PDFファイル生成中...'
              : 'PDFファイル生成完了。ZIP ダウンロードを開始します...'}
            <br />
            <br />
            <Progress type="circle" percent={pdfProgress} />
          </div>
        </div>
      </Modal>

      {/* プロジェクト一括削除 確認モーダル */}
      <Modal
        title="プロジェクト一括削除（回復不可）"
        open={!!deleteTargetProject}
        onCancel={cancelDeleteProjectModal}
        maskClosable={!deleteInProgress}
        closable={!deleteInProgress}
        footer={[
          <Button
            key="cancel"
            onClick={cancelDeleteProjectModal}
            disabled={deleteInProgress}
          >
            キャンセル
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            loading={deleteInProgress}
            onClick={confirmDeleteProject}
            disabled={
              deleteInProgress ||
              !deleteTargetProject ||
              deleteEchoInput.trim() !==
                (deleteTargetProject?.project_key || '').trim()
            }
          >
            削除を実行
          </Button>,
        ]}
        width={560}
      >
        {deleteTargetProject && (
          <div>
            <Alert
              type="warning"
              showIcon
              message="このプロジェクトに関連する以下のデータが完全に削除されます。"
              description={
                <ul style={{ margin: '0.5em 0 0 0', paddingLeft: '1.2em' }}>
                  <li>ProjectKey ドキュメント本体</li>
                  <li>EvaluationPower（受講者ごとの回答データ）全件</li>
                  <li>S3 上の生成済み PDF（個人 / 組織レポート）</li>
                </ul>
              }
              style={{ marginBottom: '1em' }}
            />
            <p style={{ marginBottom: '0.5em' }}>
              削除前にバックアップ JSON が自動でメール送信されます（社内宛、復旧用）。
            </p>
            <p style={{ marginBottom: '0.3em' }}>
              対象プロジェクトキー：
              <strong style={{ marginLeft: '0.4em' }}>
                {deleteTargetProject.project_key}
              </strong>
            </p>
            <p style={{ marginBottom: '0.5em', color: 'var(--grey-65)' }}>
              下の入力欄に上記プロジェクトキーを正確に入力してください。
            </p>
            <Input
              placeholder="プロジェクトキーを入力"
              value={deleteEchoInput}
              onChange={(e) => setDeleteEchoInput(e.target.value)}
              disabled={deleteInProgress}
              autoFocus
            />
          </div>
        )}
      </Modal>
    </PageContainer>
  );
}

export default MakeReportPage;
